import { z } from 'zod';
/**
 * The device's push token, remembered by the web app (SCO-824).
 *
 * The Expo shell posts the token to the WebView once per app process
 * (`webviewLoaded` / `webviewTokenUpdated`). Signing out reloads the page
 * (`logoutMobile` → `document.location.replace('/mobile')`), so anything held
 * in module memory is gone by the time the next person signs in, and the shell
 * does not post again until the app is force-quit. Without this record the
 * second person on a phone is never registered for push.
 *
 * The token belongs to the DEVICE, not to a user: it is deliberately kept
 * across sign-out, because the next user is the one who needs it. Only the
 * token string and the id it was last registered for are stored.
 */
var STORAGE_KEY = 'sbb_device_push_token';
var PositiveUserId = z.number().int().positive();
// Only a real Expo token is remembered. The shell can post other strings in
// the token field (it forwards its own error text when it cannot get one), and
// those must not be replayed for the next person.
var ExpoPushToken = z
    .string()
    .max(255)
    .regex(/^Expo(nent)?PushToken\[[^\]\s]+\]$/);
var RememberedTokenSchema = z.object({
    v: z.literal(1),
    token: ExpoPushToken,
    // null: seen from the shell, not yet registered for anyone.
    registeredForUserId: PositiveUserId.nullable()
});
// Used when storage is unavailable (private mode, disabled, quota): the
// record then lives for this page only, which is today's behaviour.
var memoryFallback = null;
// Set when storage refused the last write: what storage still holds is then
// older than what this page knows, so reads answer from memory.
var storageIsStale = false;
function storage() {
    try {
        return typeof window !== 'undefined' ? window.localStorage : null;
    }
    catch (_a) {
        return null;
    }
}
/**
 * The signed-in user's id as a positive whole number, or null when it is not
 * known yet. A numeric string is accepted: the store keeps raw data when its
 * own parse fails.
 */
export function toKnownUserId(id) {
    if (typeof id !== 'number' && typeof id !== 'string') {
        return null;
    }
    if (typeof id === 'string' && id.trim() === '') {
        return null;
    }
    var parsed = PositiveUserId.safeParse(Number(id));
    return parsed.success ? parsed.data : null;
}
export function isExpoPushToken(token) {
    return ExpoPushToken.safeParse(token).success;
}
export function readRememberedToken() {
    var _a;
    if (storageIsStale) {
        return memoryFallback;
    }
    try {
        var raw = (_a = storage()) === null || _a === void 0 ? void 0 : _a.getItem(STORAGE_KEY);
        if (raw) {
            var parsed = RememberedTokenSchema.safeParse(JSON.parse(raw));
            if (parsed.success) {
                return parsed.data;
            }
        }
    }
    catch (_b) {
        // Unreadable or corrupt: fall through to what this page has seen.
    }
    return memoryFallback;
}
function write(record) {
    var _a;
    memoryFallback = record;
    try {
        var store = storage();
        if (!store) {
            storageIsStale = true;
            return;
        }
        store.setItem(STORAGE_KEY, JSON.stringify(record));
        storageIsStale = false;
    }
    catch (_b) {
        // Storage refused the write; the in-memory copy serves this page.
        storageIsStale = true;
        // What storage still holds is now wrong, and the next page load would
        // trust it. Remove it so the next load starts from "nothing remembered".
        // If even that is refused, a reload trusts the old record: accepted, it
        // cannot be helped from here.
        try {
            (_a = storage()) === null || _a === void 0 ? void 0 : _a.removeItem(STORAGE_KEY);
        }
        catch (_c) {
            // Nothing more to do.
        }
    }
}
/** A token arrived from the shell. Keeps the registration mark only if it is the same token. */
export function rememberDeviceToken(token) {
    if (!isExpoPushToken(token)) {
        return;
    }
    var current = readRememberedToken();
    write({
        v: 1,
        token: token,
        registeredForUserId: current && current.token === token ? current.registeredForUserId : null
    });
}
/**
 * A registration of this token is about to be sent. Until the backend
 * confirms it, nobody is recorded as its owner: if the answer is lost, the
 * backend may hold the new user while this page still names the old one, and
 * the old user signing back in would be skipped (SCO-824 review).
 */
export function markTokenUnconfirmed(token) {
    var current = readRememberedToken();
    if (current && current.token === token && current.registeredForUserId !== null) {
        write({ v: 1, token: token, registeredForUserId: null });
    }
}
/**
 * True only inside the Expo shell: react-native-webview injects this bridge
 * when the shell's WebView sets `onMessage` (it does). The mobile pages also
 * load in an ordinary browser, where any window that opened the page can post
 * a `message`; nothing from there is ever remembered. (Replay is not gated:
 * only the Home tab has the bridge, see `registerRememberedTokenIfOwnerChanged`.)
 */
export function isInsideNativeShell() {
    try {
        return (typeof window !== 'undefined' &&
            typeof window.ReactNativeWebView !==
                'undefined');
    }
    catch (_a) {
        return false;
    }
}
/** The backend accepted this token for this user. */
export function markTokenRegistered(token, userId) {
    if (!isExpoPushToken(token)) {
        return;
    }
    var current = readRememberedToken();
    // A newer token arrived while the request was in flight: leave it unmarked.
    if (current && current.token !== token) {
        return;
    }
    write({ v: 1, token: token, registeredForUserId: userId });
}
/** Test seam: forget the in-memory copy (storage is cleared by the test). */
export function resetDevicePushTokenForTests() {
    memoryFallback = null;
    storageIsStale = false;
}
